/**
 * Monthly Breakdown Row Component
 * Card-style component showing month data - expands to show details in tabbed dialog
 */
import React, { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { ChevronDown, ChevronUp, Paperclip, CheckCircle, AlertCircle, XCircle, PoundSterling, Save, X, Calendar, Ship, FileText, BarChart3 } from 'lucide-react';
import ComparisonSection from './ComparisonSection';
import TripsSection from './TripsSection';
import DocumentSection from './DocumentSection';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get match status badge
const getMatchStatusBadge = (comparison) => {
  if (!comparison) return null;
  
  switch (comparison.match_status) {
    case 'match':
      return (
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Match
        </Badge>
      );
    case 'close':
      return (
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          <AlertCircle className="h-3 w-3 mr-1" />
          Close (within 5%)
        </Badge>
      );
    case 'mismatch':
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          Mismatch
        </Badge>
      );
    case 'no_wage_slip':
      return (
        <Badge variant="outline" className="border-slate-600 text-slate-400">
          No wage slip uploaded
        </Badge>
      );
    default:
      return null;
  }
};

const MonthlyBreakdownRow = ({
  monthData,
  selectedYear,
  tripsForMonth,
  docsForMonth,
  isExpanded,
  comparison,
  extractingDoc,
  onToggleExpand,
  onUploadClick,
  onPreview,
  onDownload,
  onExtract,
  onEdit,
  onDelete,
  gbpReceivedData,
  onGbpReceivedUpdate,
  onEditTrip,
  onViewTrip,
  onExtractWithPassword,
  onSavePassword
}) => {  const [gbpReceived, setGbpReceived] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // PAYE deduction fields
  const [payslipData, setPayslipData] = useState({
    gross_pay: '', income_tax_deducted: '', ni_deducted: '', pension_deducted: '', student_loan_deducted: ''
  });
  const [editingPayslip, setEditingPayslip] = useState(false);
  
  // Aggregate tax data from wage slips for this month
  const wageSlipTax = React.useMemo(() => {
    const slips = docsForMonth.filter(d => d.document_type === 'wage_slip' && d.extracted_data);
    if (slips.length === 0) return null;
    
    let tax = 0, ni = 0, pension = 0, gross = 0, net = 0;
    slips.forEach(s => {
      const d = s.extracted_data;
      tax += d.income_tax || 0;
      ni += d.national_insurance || 0;
      pension += d.pension_contribution || 0;
      gross += d.gross_earnings || 0;
      net += d.net_earnings || 0;
    });
    
    return { tax, ni, pension, gross, net, count: slips.length };
  }, [docsForMonth]);
  
  // Initialize gbpReceived from props
  useEffect(() => {
    const savedValue = gbpReceivedData?.[monthData.monthKey]?.gbp_received;
    setGbpReceived(savedValue !== null && savedValue !== undefined ? savedValue.toString() : '');
    // Initialize payslip fields
    const rec = gbpReceivedData?.[monthData.monthKey];
    if (rec) {
      setPayslipData({
        gross_pay: rec.gross_pay != null ? rec.gross_pay.toString() : '',
        income_tax_deducted: rec.income_tax_deducted != null ? rec.income_tax_deducted.toString() : '',
        ni_deducted: rec.ni_deducted != null ? rec.ni_deducted.toString() : '',
        pension_deducted: rec.pension_deducted != null ? rec.pension_deducted.toString() : '',
        student_loan_deducted: rec.student_loan_deducted != null ? rec.student_loan_deducted.toString() : '',
      });
    }
  }, [gbpReceivedData, monthData.monthKey]);
  
  const handleSaveGbpReceived = async () => {
    setSaving(true);
    try {
      const value = gbpReceived === '' ? null : parseFloat(gbpReceived);
      await axios.put(`${API_URL}/api/monthly-gbp-received/${monthData.monthKey}`, {
        gbp_received: value,
        gross_pay: payslipData.gross_pay === '' ? null : parseFloat(payslipData.gross_pay),
        income_tax_deducted: payslipData.income_tax_deducted === '' ? null : parseFloat(payslipData.income_tax_deducted),
        ni_deducted: payslipData.ni_deducted === '' ? null : parseFloat(payslipData.ni_deducted),
        pension_deducted: payslipData.pension_deducted === '' ? null : parseFloat(payslipData.pension_deducted),
        student_loan_deducted: payslipData.student_loan_deducted === '' ? null : parseFloat(payslipData.student_loan_deducted),
      });
      toast.success('Payslip data saved');
      setIsEditing(false);
      setEditingPayslip(false);
      if (onGbpReceivedUpdate) {
        onGbpReceivedUpdate();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancelEdit = () => {
    const rec = gbpReceivedData?.[monthData.monthKey];
    const savedValue = rec?.gbp_received;
    setGbpReceived(savedValue !== null && savedValue !== undefined ? savedValue.toString() : '');
    setPayslipData({
      gross_pay: rec?.gross_pay != null ? rec.gross_pay.toString() : '',
      income_tax_deducted: rec?.income_tax_deducted != null ? rec.income_tax_deducted.toString() : '',
      ni_deducted: rec?.ni_deducted != null ? rec.ni_deducted.toString() : '',
      pension_deducted: rec?.pension_deducted != null ? rec.pension_deducted.toString() : '',
      student_loan_deducted: rec?.student_loan_deducted != null ? rec.student_loan_deducted.toString() : '',
    });
    setIsEditing(false);
    setEditingPayslip(false);
  };
  
  const savedGbpReceived = gbpReceivedData?.[monthData.monthKey]?.gbp_received;
  const hasGbpReceived = savedGbpReceived !== null && savedGbpReceived !== undefined;
  const difference = hasGbpReceived ? savedGbpReceived - monthData.earnings : null;

  // Check if any manual payslip data has been saved
  const savedRecord = gbpReceivedData?.[monthData.monthKey];
  const hasManualPayslip = savedRecord && (
    savedRecord.income_tax_deducted != null || savedRecord.ni_deducted != null ||
    savedRecord.pension_deducted != null || savedRecord.gross_pay != null
  );

  // Get border color based on match status
  const getBorderColor = () => {
    if (!comparison) return 'border-l-slate-600';
    if (comparison.match_status === 'match') return 'border-l-amber-500';
    if (comparison.match_status === 'close') return 'border-l-amber-500';
    if (comparison.match_status === 'mismatch') return 'border-l-red-500';
    return 'border-l-slate-600';
  };

  return (
    <>
      {/* Card View */}
      <div 
        className={`border border-slate-700 rounded-lg overflow-hidden cursor-pointer hover:border-slate-500 hover:bg-slate-700/30 transition-all border-l-4 ${getBorderColor()}`}
        onClick={onToggleExpand}
        data-testid={`month-card-${monthData.monthKey}`}
      >
        <div className="p-4">
          {/* Month Header */}
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-white text-lg">{monthData.fullMonth.split(' ')[0]}</p>
            <p className="text-xs text-slate-500">{selectedYear?.split('/')[0]}</p>
          </div>
          
          {/* Earnings - Larger and more prominent */}
          <div className="mb-3">
            <p className={`text-3xl font-bold tracking-tight ${monthData.earnings > 0 ? 'text-amber-400' : 'text-slate-600'}`}>
              £{monthData.earnings.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            {hasGbpReceived && (
              <p className="text-xs text-slate-400 mt-1">
                Received: £{savedGbpReceived.toLocaleString('en-GB', { minimumFractionDigits: 0 })}
              </p>
            )}
          </div>
          
          {/* Stats Row */}
          <div className="flex items-center gap-3 text-xs text-slate-400 mb-2">
            {tripsForMonth.length > 0 && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {tripsForMonth.length} trip{tripsForMonth.length !== 1 ? 's' : ''}
              </span>
            )}
            {docsForMonth.length > 0 && (
              <span className="flex items-center gap-1 text-blue-400">
                <Paperclip className="h-3 w-3" />
                {docsForMonth.length}
              </span>
            )}
          </div>

          {/* Tax/NI from wage slips */}
          {wageSlipTax && (
            <div className="text-[10px] text-slate-500 space-y-0.5 mb-2">
              {wageSlipTax.tax > 0 && <p>Tax: <span className="text-red-400">£{wageSlipTax.tax.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span></p>}
              {wageSlipTax.ni > 0 && <p>NI: <span className="text-red-400">£{wageSlipTax.ni.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span></p>}
              {wageSlipTax.pension > 0 && <p>Pension: <span className="text-orange-400">£{wageSlipTax.pension.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span></p>}
            </div>
          )}
          
          {/* Match Status Badge */}
          <div className="flex flex-wrap gap-1">
            {hasGbpReceived && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                <PoundSterling className="h-3 w-3" />
              </Badge>
            )}
            {comparison && (
              <div className="w-full mt-1">
                {getMatchStatusBadge(comparison)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Dialog - Tabbed Interface */}
      <Dialog open={isExpanded} onOpenChange={(open) => !open && onToggleExpand()}>
        <DialogContent className="bg-slate-800 border-slate-700 w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="monthly-details-dialog">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-400" />
              {monthData.fullMonth}
              <span className="text-slate-400 font-normal text-sm ml-1">({selectedYear})</span>
            </DialogTitle>
            {/* Quick Stats Row */}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <span className={`text-xl font-bold ${monthData.earnings > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                  £{monthData.earnings.toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                </span>
                <span className="text-xs text-slate-500">gross</span>
              </div>
              {hasGbpReceived && (
                <div className="flex items-center gap-1.5 border-l border-slate-600 pl-4">
                  <span className="text-lg font-semibold text-blue-400">
                    £{savedGbpReceived.toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                  </span>
                  <span className="text-xs text-slate-500">received</span>
                </div>
              )}
            </div>
          </DialogHeader>
          
          <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
            <TabsList className="bg-slate-900 border border-slate-700 p-1 w-full grid grid-cols-3">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-slate-400"
                data-testid="tab-overview"
              >
                <BarChart3 className="h-4 w-4 mr-1.5" />
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="trips" 
                className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-slate-400"
                data-testid="tab-trips"
              >
                <Ship className="h-4 w-4 mr-1.5" />
                Trips
                {tripsForMonth.length > 0 && (
                  <span className="ml-1.5 text-xs bg-slate-700 px-1.5 py-0.5 rounded-full">{tripsForMonth.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="documents" 
                className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-slate-400"
                data-testid="tab-documents"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Docs
                {docsForMonth.length > 0 && (
                  <span className="ml-1.5 text-xs bg-blue-600 px-1.5 py-0.5 rounded-full">{docsForMonth.length}</span>
                )}
              </TabsTrigger>
            </TabsList>
            
            {/* Scrollable Tab Content Area */}
            <div className="flex-1 overflow-y-auto mt-4 pr-1">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0 space-y-4" data-testid="tab-content-overview">
                {/* GBP Received Section */}
                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <PoundSterling className="h-4 w-4 text-amber-400" />
                      <span className="text-sm font-medium text-white">Actual GBP Received</span>
                    </div>
                    {!isEditing && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditing(true);
                        }}
                        className="h-7 text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        data-testid="edit-gbp-received-btn"
                      >
                        {hasGbpReceived ? 'Edit' : 'Add Amount'}
                      </Button>
                    )}
                  </div>
                  
                  {isEditing ? (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                        <Input
                          type="number"
                          value={gbpReceived}
                          onChange={(e) => setGbpReceived(e.target.value)}
                          placeholder="Enter amount received"
                          className="pl-7 bg-slate-800 border-slate-600 text-white"
                          step="0.01"
                          min="0"
                          data-testid="gbp-received-input"
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={handleSaveGbpReceived}
                        disabled={saving}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="save-gbp-btn"
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEdit}
                        className="text-slate-400 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {hasGbpReceived ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-slate-800 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Gross (Calculated)</p>
                            <p className="text-lg font-semibold text-white">
                              £{monthData.earnings.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                            <p className="text-xs text-amber-400 mb-1">Received</p>
                            <p className="text-lg font-semibold text-amber-400">
                              £{savedGbpReceived.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          {difference !== null && Math.abs(difference) > 0.01 && (
                            <div className="col-span-2 flex items-center justify-between p-2 bg-slate-800 rounded-lg">
                              <span className="text-sm text-slate-400">Difference</span>
                              <span className={`font-semibold ${difference > 0 ? 'text-amber-400' : 'text-amber-400'}`}>
                                {difference > 0 ? '+' : ''}£{difference.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">
                          No actual GBP amount entered. Click "Add Amount" to record what you received after currency conversion.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Earnings Comparison Section */}
                <ComparisonSection comparison={comparison} />
                
                {/* Quick Summary */}
                {(tripsForMonth.length > 0 || docsForMonth.length > 0) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Ship className="h-4 w-4" />
                        <span className="text-xs uppercase">Trips</span>
                      </div>
                      <p className="text-lg font-semibold text-white">{tripsForMonth.length}</p>
                    </div>
                    <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <FileText className="h-4 w-4" />
                        <span className="text-xs uppercase">Documents</span>
                      </div>
                      <p className="text-lg font-semibold text-white">{docsForMonth.length}</p>
                    </div>
                  </div>
                )}

                {/* Tax Deductions from Wage Slips */}
                {wageSlipTax && (
                  <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <PoundSterling className="h-4 w-4 text-red-400" />
                      <span className="text-sm font-medium text-white">Deductions (from {wageSlipTax.count} wage slip{wageSlipTax.count !== 1 ? 's' : ''})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {wageSlipTax.gross > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Gross Pay</p>
                          <p className="text-sm font-semibold text-white">£{wageSlipTax.gross.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      <div className="p-2.5 bg-slate-800 rounded-lg">
                        <p className="text-[10px] text-slate-500 uppercase mb-0.5">Income Tax</p>
                        <p className="text-sm font-semibold text-red-400">£{wageSlipTax.tax.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="p-2.5 bg-slate-800 rounded-lg">
                        <p className="text-[10px] text-slate-500 uppercase mb-0.5">National Ins.</p>
                        <p className="text-sm font-semibold text-red-400">£{wageSlipTax.ni.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                      </div>
                      {wageSlipTax.pension > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Pension</p>
                          <p className="text-sm font-semibold text-orange-400">£{wageSlipTax.pension.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      {wageSlipTax.net > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Net Pay</p>
                          <p className="text-sm font-semibold text-emerald-400">£{wageSlipTax.net.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Manual Payslip Entry */}
                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700" data-testid="payslip-entry-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <PoundSterling className="h-4 w-4 text-sky-400" />
                      <span className="text-sm font-medium text-white">Actual Payslip Amounts</span>
                    </div>
                    {!editingPayslip && (
                      <Button size="sm" variant="outline"
                        onClick={(e) => { e.stopPropagation(); setEditingPayslip(true); setIsEditing(true); }}
                        className="h-7 text-xs border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                        data-testid="edit-payslip-btn">
                        {hasManualPayslip ? 'Edit' : 'Enter Amounts'}
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Enter actual amounts from your payslip. For PAYE employees, enter tax, NI and pension deducted. For gross day-rate workers, just enter gross pay and GBP received.
                  </p>

                  {editingPayslip ? (
                    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] text-slate-400 uppercase">Gross Pay</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={payslipData.gross_pay}
                              onChange={e => setPayslipData(p => ({...p, gross_pay: e.target.value}))}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-gross-input" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400 uppercase">GBP Received</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={gbpReceived}
                              onChange={e => setGbpReceived(e.target.value)}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-gbp-input" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-red-400 uppercase">Income Tax</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={payslipData.income_tax_deducted}
                              onChange={e => setPayslipData(p => ({...p, income_tax_deducted: e.target.value}))}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-tax-input" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-red-400 uppercase">National Insurance</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={payslipData.ni_deducted}
                              onChange={e => setPayslipData(p => ({...p, ni_deducted: e.target.value}))}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-ni-input" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-orange-400 uppercase">Pension</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={payslipData.pension_deducted}
                              onChange={e => setPayslipData(p => ({...p, pension_deducted: e.target.value}))}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-pension-input" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-purple-400 uppercase">Student Loan</label>
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                            <Input type="number" value={payslipData.student_loan_deducted}
                              onChange={e => setPayslipData(p => ({...p, student_loan_deducted: e.target.value}))}
                              placeholder="0.00" step="0.01" min="0"
                              className="pl-6 bg-slate-800 border-slate-600 text-white h-8 text-sm"
                              data-testid="payslip-student-input" />
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveGbpReceived} disabled={saving}
                          className="bg-sky-600 hover:bg-sky-700 flex-1" data-testid="save-payslip-btn">
                          <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? 'Saving...' : 'Save All'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}
                          className="text-slate-400 hover:text-white">
                          <X className="h-3.5 w-3.5 mr-1.5" />Cancel
                        </Button>
                      </div>
                    </div>
                  ) : hasManualPayslip ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {savedRecord?.gross_pay != null && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Gross Pay</p>
                          <p className="text-sm font-semibold text-white">£{savedRecord.gross_pay.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      {savedRecord?.income_tax_deducted != null && savedRecord.income_tax_deducted > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Income Tax</p>
                          <p className="text-sm font-semibold text-red-400">£{savedRecord.income_tax_deducted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      {savedRecord?.ni_deducted != null && savedRecord.ni_deducted > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">National Ins.</p>
                          <p className="text-sm font-semibold text-red-400">£{savedRecord.ni_deducted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      {savedRecord?.pension_deducted != null && savedRecord.pension_deducted > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Pension</p>
                          <p className="text-sm font-semibold text-orange-400">£{savedRecord.pension_deducted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                      {savedRecord?.student_loan_deducted != null && savedRecord.student_loan_deducted > 0 && (
                        <div className="p-2.5 bg-slate-800 rounded-lg">
                          <p className="text-[10px] text-slate-500 uppercase mb-0.5">Student Loan</p>
                          <p className="text-sm font-semibold text-purple-400">£{savedRecord.student_loan_deducted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No payslip amounts entered yet. Click "Enter Amounts" to record your actual deductions.</p>
                  )}
                </div>
              </TabsContent>

              {/* Trips Tab */}
              <TabsContent value="trips" className="mt-0" data-testid="tab-content-trips">
                {tripsForMonth.length > 0 ? (
                  <TripsSection 
                    trips={tripsForMonth} 
                    onEditTrip={onEditTrip}
                    onViewTrip={onViewTrip}
                  />
                ) : (
                  <div className="text-center py-8">
                    <Ship className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No trips recorded for this month</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-0" data-testid="tab-content-documents">
                <DocumentSection
                  documents={docsForMonth}
                  comparison={comparison}
                  monthKey={monthData.monthKey}
                  extractingDoc={extractingDoc}
                  onUploadClick={onUploadClick}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  onExtract={onExtract}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onExtractWithPassword={onExtractWithPassword}
                  onSavePassword={onSavePassword}
                />
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthlyBreakdownRow;
